import { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import {
    Accordion, AccordionButton, AccordionItem, AccordionPanel,
    Badge, Box, Button, Flex, Grid, Input, Spinner, Text,
    useColorModeValue, useToast
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Container, Heading, Icon, IconButton, Tag } from "components"
import { API } from "config"
import { useSuppliers } from "hooks"

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    : '—'

const FieldLabel = ({ children, required, hint }) => (
    <Box mb={1}>
        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider"
            color="gray.500" _dark={{ color: 'gray.400' }}>
            {children}{required && <Box as="span" color="red.400" ml={0.5}>*</Box>}
        </Text>
        {hint && <Text fontSize="2xs" color="gray.400" mt={0.5}>{hint}</Text>}
    </Box>
)

// ─── Supplier search ──────────────────────────────────────────────────────────

const SupplierSearch = ({ value, onChange }) => {
    const [term, setTerm] = useState(value || '')
    const [open, setOpen] = useState(false)
    const suppliers       = useSuppliers()
    const allSuppliers    = suppliers.data ?? []

    useEffect(() => { setTerm(value || '') }, [value])

    const filtered = term.length >= 2
        ? allSuppliers.filter(s => s.name?.toLowerCase().includes(term.toLowerCase())).slice(0, 8)
        : []

    const handleSelect = (s) => { onChange(s.name); setTerm(s.name); setOpen(false) }
    const handleChange = (e) => { const v = e.target.value; setTerm(v); onChange(v); setOpen(true) }

    return (
        <Box position="relative">
            <Input size="md" value={term}
                placeholder="Busque ou digite o nome do fornecedor..."
                onChange={handleChange}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 180)}
            />
            {open && filtered.length > 0 && (
                <Box position="absolute" top="calc(100% + 4px)" left={0} right={0}
                    bg="white" border="1px solid" borderColor="gray.200"
                    _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                    borderRadius="md" boxShadow="lg" zIndex={1500} maxH="200px" overflowY="auto"
                >
                    {filtered.map(s => (
                        <Box key={s.id} px={4} py={2.5} cursor="pointer"
                            _hover={{ bg: 'blue.50' }} _dark={{ _hover: { bg: 'gray.700' } }}
                            onMouseDown={() => handleSelect(s)}
                        >
                            <Text fontSize="sm" fontWeight="medium">{s.name}</Text>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

// ─── Hook de listagem de provisórias ──────────────────────────────────────────

const useProvisionalInvoices = () =>
    useQuery({
        queryKey: ['invoices-provisional'],
        queryFn:  async () => {
            const res = await API.get('/invoices/provisional')
            return res.data
        },
        staleTime: 60_000,
    })

// ─── Formulário de conciliação por NF-e ───────────────────────────────────────

const ReconcileForm = ({ invoice, onDone }) => {
    const toast = useToast()
    const [loading, setLoading] = useState(false)
    const [errors,  setErrors]  = useState({})
    const [file,    setFile]    = useState(null)

    const [form, setForm] = useState({
        number:            '',
        supplier_name:     invoice.supplier_name === 'Fornecedor não informado' ? '' : (invoice.supplier_name ?? ''),
        supplier_document: '',
        issued_at:         '',
        key:               '',
        notes:             '',
    })

    const [lots, setLots] = useState(
        (invoice.items ?? []).map(it => ({
            invoice_item_id: it.id,
            product_name:    it.product?.name ?? it.product_id,
            current_lot:     it.lots?.[0]?.lot_number ?? '—',
            lot_number:      '',
            expires_at:      it.lots?.[0]?.expires_at?.slice(0, 10) ?? '',
        }))
    )

    const set    = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: null })) }
    const setLot = (i, patch) => setLots(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l))

    const validate = () => {
        const e = {}
        if (!form.number.trim())       e.number       = 'Obrigatório'
        if (!form.supplier_name.trim()) e.supplier_name = 'Obrigatório'
        if (form.key && form.key.length !== 44) e.key = 'Deve ter 44 dígitos'
        const missingLots = lots.filter(l => !l.lot_number.trim())
        if (missingLots.length) e.lots = 'Preencha o número do lote para todos os itens'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const handleSubmit = async () => {
        if (!validate()) return
        setLoading(true)

        const fd = new FormData()
        fd.append('number',        form.number)
        fd.append('supplier_name', form.supplier_name)
        if (form.supplier_document) fd.append('supplier_document', form.supplier_document)
        if (form.issued_at)         fd.append('issued_at', form.issued_at)
        if (form.key)               fd.append('key', form.key)
        if (form.notes)             fd.append('notes', form.notes)
        if (file)                   fd.append('file', file)
        lots.forEach((l, i) => {
            fd.append(`items[${i}][invoice_item_id]`, l.invoice_item_id)
            fd.append(`items[${i}][lot_number]`,      l.lot_number)
            if (l.expires_at) fd.append(`items[${i}][expires_at]`, l.expires_at)
        })

        try {
            await API.post(`/invoices/${invoice.id}/reconcile`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            toast({ title: 'Conciliação realizada com sucesso', status: 'success', duration: 4000 })
            onDone()
        } catch (err) {
            const msg = err?.response?.data?.message ?? 'Erro ao conciliar NF-e'
            toast({ title: msg, status: 'error', duration: 6000 })
        } finally {
            setLoading(false)
        }
    }

    const cardBg      = useColorModeValue('white', 'gray.800')
    const lotBorder   = useColorModeValue('gray.100', 'gray.700')

    return (
        <Box p={5}>
            {/* Dados da NF-e real */}
            <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider"
                color="gray.500" _dark={{ color: 'gray.400' }} mb={4}>
                Dados da NF-e Real
            </Text>

            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4} mb={5}>
                <Box>
                    <FieldLabel required>Número da NF-e</FieldLabel>
                    <Input size="md" value={form.number} placeholder="000.064.179"
                        onChange={e => set('number', e.target.value)}
                        isInvalid={!!errors.number} />
                    {errors.number && <Text fontSize="2xs" color="red.500" mt={0.5}>{errors.number}</Text>}
                </Box>
                <Box>
                    <FieldLabel required>Fornecedor</FieldLabel>
                    <SupplierSearch
                        value={form.supplier_name}
                        onChange={v => set('supplier_name', v)}
                    />
                    {errors.supplier_name && <Text fontSize="2xs" color="red.500" mt={0.5}>{errors.supplier_name}</Text>}
                </Box>
                <Box>
                    <FieldLabel>CNPJ do Fornecedor</FieldLabel>
                    <Input size="md" value={form.supplier_document} placeholder="00.000.000/0001-00"
                        onChange={e => set('supplier_document', e.target.value)} />
                </Box>
                <Box>
                    <FieldLabel>Data de Emissão</FieldLabel>
                    <Input size="md" type="date" value={form.issued_at}
                        onChange={e => set('issued_at', e.target.value)} />
                </Box>
                <Box>
                    <FieldLabel>Chave de Acesso</FieldLabel>
                    <Input size="md" value={form.key} maxLength={44} placeholder="52260611..."
                        onChange={e => set('key', e.target.value.replace(/\D/g, '').slice(0, 44))}
                        isInvalid={!!errors.key} fontFamily="mono" fontSize="xs" />
                    <Text fontSize="2xs" color="gray.400" mt={0.5}>44 dígitos numéricos</Text>
                    {errors.key && <Text fontSize="2xs" color="red.500" mt={0.5}>{errors.key}</Text>}
                </Box>
                <Box>
                    <FieldLabel>Documento Fiscal</FieldLabel>
                    {file ? (
                        <Flex align="center" gap={2}
                            border="1px solid" borderColor="teal.300" bg="teal.50"
                            _dark={{ bg: 'teal.900', borderColor: 'teal.500' }}
                            borderRadius="md" px={3} py={1.5}
                        >
                            <Icon name="check" boxSize={4} color="teal.500" />
                            <Text fontSize="xs" flex={1} noOfLines={1}>{file.name}</Text>
                            <Box as="button" onClick={() => setFile(null)} color="gray.400"
                                _hover={{ color: 'red.400' }}>
                                <Icon name="cancel" boxSize={3.5} />
                            </Box>
                        </Flex>
                    ) : (
                        <>
                            <Button size="md" variant="outline" w="100%"
                                leftIcon={<Icon name="upload" boxSize={3.5} />}
                                onClick={() => document.getElementById(`file-${invoice.id}`)?.click()}
                            >
                                Selecionar arquivo
                            </Button>
                            <input id={`file-${invoice.id}`} type="file" style={{ display: 'none' }}
                                accept=".xml,.pdf,.jpg,.jpeg,.png,.webp,.mp4,.mov,.avi"
                                onChange={e => setFile(e.target.files?.[0] ?? null)} />
                            <Text fontSize="2xs" color="gray.400" mt={0.5}>XML, PDF, foto ou vídeo</Text>
                        </>
                    )}
                </Box>
            </Grid>

            {/* Lotes */}
            <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider"
                color="gray.500" _dark={{ color: 'gray.400' }} mb={3}>
                Números de Lote Reais
            </Text>

            {errors.lots && (
                <Text fontSize="xs" color="red.500" mb={3}>{errors.lots}</Text>
            )}

            <Flex direction="column" gap={3} mb={5}>
                {lots.map((lot, i) => (
                    <Box key={lot.invoice_item_id}
                        bg={cardBg} border="1px solid"
                        borderColor={lotBorder}
                        borderRadius="lg" p={4}
                    >
                        <Flex align="center" gap={2} mb={3}>
                            <Icon name="product" boxSize={4} color="teal.400" />
                            <Text fontSize="sm" fontWeight="semibold">{lot.product_name}</Text>
                        </Flex>
                        <Grid templateColumns={{ base: '1fr', sm: 'auto 1fr 1fr' }} gap={3} alignItems="end">
                            <Box>
                                <FieldLabel>Lote atual (provisório)</FieldLabel>
                                <Flex align="center" gap={2} h="40px">
                                    <Text fontSize="xs" fontFamily="mono" color="purple.500"
                                        fontWeight="semibold">{lot.current_lot}</Text>
                                    <Icon name="right" boxSize={3} color="gray.400" />
                                </Flex>
                            </Box>
                            <Box>
                                <FieldLabel required>Número real do lote</FieldLabel>
                                <Input size="md" value={lot.lot_number}
                                    placeholder="Ex: 25.0312A" fontFamily="mono"
                                    onChange={e => setLot(i, { lot_number: e.target.value })}
                                    isInvalid={!!errors.lots && !lot.lot_number.trim()} />
                            </Box>
                            <Box>
                                <FieldLabel>Validade</FieldLabel>
                                <Input size="md" type="date" value={lot.expires_at}
                                    onChange={e => setLot(i, { expires_at: e.target.value })} />
                            </Box>
                        </Grid>
                    </Box>
                ))}
            </Flex>

            {/* Observações */}
            <Box mb={5}>
                <FieldLabel>Observações</FieldLabel>
                <Input size="md" as="textarea" rows={2}
                    value={form.notes} placeholder="Informações adicionais..."
                    onChange={e => set('notes', e.target.value)}
                    style={{ resize: 'vertical', minHeight: '56px', paddingTop: '8px' }} />
            </Box>

            <Flex justify="flex-end" gap={3}>
                <Button size="sm" colorScheme="teal" isLoading={loading}
                    leftIcon={<Icon name="check" boxSize={3.5} />}
                    loadingText="Conciliando..."
                    onClick={handleSubmit}
                >
                    Conciliar NF-e
                </Button>
            </Flex>
        </Box>
    )
}

// ─── Linha da NF-e provisória ──────────────────────────────────────────────────

const ProvisionalRow = ({ invoice, onReconciled }) => {
    const navigate     = useNavigate()
    const queryClient  = useQueryClient()
    const labelColor   = useColorModeValue('gray.500', 'gray.400')
    const accordBg     = useColorModeValue('gray.50',  'gray.750')
    const accordBorder = useColorModeValue('gray.100', 'gray.700')
    const itemBorder   = useColorModeValue('purple.200', 'purple.700')
    const itemBg       = useColorModeValue('white', 'gray.800')

    const handleDone = () => {
        queryClient.invalidateQueries({ queryKey: ['invoices-provisional'] })
        queryClient.invalidateQueries({ queryKey: ['invoices-list'] })
        onReconciled?.()
    }

    return (
        <AccordionItem
            border="1px solid"
            borderColor={itemBorder}
            borderRadius="2xl"
            mb={3}
            overflow="hidden"
            _last={{ mb: 0 }}
        >
            {({ isExpanded }) => (
                <>
                    <AccordionButton
                        px={6} py={4}
                        bg={isExpanded ? accordBg : itemBg}
                        _hover={{ bg: accordBg }}
                        borderRadius={isExpanded ? '0' : '2xl'}
                    >
                        <Flex flex={1} align="center" gap={4} flexWrap="wrap" textAlign="left">
                            {/* Ícone */}
                            <Flex w={9} h={9} borderRadius="xl" flexShrink={0}
                                align="center" justify="center"
                                bg="purple.50" _dark={{ bg: 'purple.900' }}
                            >
                                <Icon name="stock" boxSize={4} color="purple.400" />
                            </Flex>

                            {/* Número + núcleo */}
                            <Box flex={1} minW="150px">
                                <Flex align="center" gap={2} flexWrap="wrap">
                                    <Text fontWeight="700" fontSize="sm" fontFamily="mono">
                                        {invoice.number}
                                    </Text>
                                    <Badge colorScheme="purple" borderRadius="full"
                                        px={2.5} fontSize="2xs">
                                        Provisória
                                    </Badge>
                                </Flex>
                                <Text fontSize="xs" color={labelColor} mt={0.5}>
                                    {invoice.distributor?.name ?? '—'} · {fmtDate(invoice.created_at)}
                                </Text>
                            </Box>

                            {/* Itens */}
                            <Flex gap={2} align="center" flexWrap="wrap">
                                <Tag colorScheme="gray" size="sm">
                                    {invoice.items?.length ?? 0} item(s)
                                </Tag>
                                <Box
                                    as="button"
                                    onClick={e => { e.stopPropagation(); navigate(`/stock/invoices/${invoice.id}`) }}
                                    fontSize="xs" color="blue.400"
                                    _hover={{ textDecoration: 'underline' }}
                                    fontWeight="medium"
                                >
                                    Ver detalhes →
                                </Box>
                            </Flex>
                        </Flex>
                        <Box ml={2} color={labelColor}>
                            <Icon name={isExpanded ? 'up' : 'down'} boxSize={4} />
                        </Box>
                    </AccordionButton>

                    <AccordionPanel
                        p={0}
                        bg={accordBg}
                        borderTop="1px solid"
                        borderColor={accordBorder}
                    >
                        <ReconcileForm invoice={invoice} onDone={handleDone} />
                    </AccordionPanel>
                </>
            )}
        </AccordionItem>
    )
}

// ─── Página principal ──────────────────────────────────────────────────────────

export default function Reconciliation() {
    const { data, isFetching, refetch } = useProvisionalInvoices()
    const [search, setSearch] = useState('')

    const all = data?.data ?? []
    const filtered = search.trim()
        ? all.filter(inv =>
            inv.number?.toLowerCase().includes(search.toLowerCase()) ||
            inv.distributor?.name?.toLowerCase().includes(search.toLowerCase())
        )
        : all

    const cardBg      = useColorModeValue('white', 'gray.800')
    const cardBorder  = useColorModeValue('gray.200', 'gray.700')
    const filterBg    = useColorModeValue('gray.50', 'gray.700')
    const filterBorder = useColorModeValue('gray.100', 'gray.600')
    const labelColor  = useColorModeValue('gray.500', 'gray.400')

    return (
        <Container maxW="90%" mt={10}>
            <Heading title="Conciliações de NF-e">
                <IconButton size="sm" icon="refresh" isLoading={isFetching} onClick={refetch} />
            </Heading>

            <Text color={labelColor} fontSize="sm" mt={1} mb={6}>
                Entradas provisórias aguardando vinculação com a NF-e real. Expanda para preencher os dados fiscais.
            </Text>

            {/* Sumário */}
            <Flex gap={4} mb={6} flexWrap="wrap">
                <Box bg={cardBg} border="1px solid" borderColor={cardBorder}
                    borderRadius="xl" px={5} py={4} minW="180px">
                    <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                        letterSpacing="wider" color={labelColor} mb={1}>
                        Pendentes de conciliação
                    </Text>
                    <Flex align="baseline" gap={2}>
                        <Text fontSize="3xl" fontWeight="bold" color="purple.500" lineHeight="1">
                            {all.length}
                        </Text>
                        <Text fontSize="sm" color={labelColor}>entrada(s)</Text>
                    </Flex>
                </Box>
            </Flex>

            {/* Busca */}
            <Box bg={filterBg} border="1px solid" borderColor={filterBorder}
                borderRadius="xl" p={4} mb={5}
            >
                <Flex align="center" gap={3}>
                    <Icon name="search" boxSize={4} color={labelColor} flexShrink={0} />
                    <Input variant="unstyled" placeholder="Buscar por número ou núcleo..." fontSize="sm"
                        value={search} onChange={e => setSearch(e.target.value)} />
                    {search && (
                        <Box as="button" onClick={() => setSearch('')} color={labelColor}
                            _hover={{ color: 'red.400' }} transition="color 0.15s">
                            <Icon name="cancel" boxSize={4} />
                        </Box>
                    )}
                </Flex>
            </Box>

            {/* Conteúdo */}
            {isFetching && !data ? (
                <Flex justify="center" py={20}><Spinner size="lg" color="purple.400" /></Flex>
            ) : all.length === 0 ? (
                <Flex
                    direction="column" align="center" justify="center" gap={4} py={20}
                    bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl"
                >
                    <Box w={16} h={16} borderRadius="2xl" bg="green.50"
                        _dark={{ bg: 'green.900' }}
                        display="flex" alignItems="center" justifyContent="center"
                    >
                        <Icon name="check" boxSize={8} color="green.400" />
                    </Box>
                    <Box textAlign="center">
                        <Text fontWeight="semibold" fontSize="md">Nenhuma pendência!</Text>
                        <Text fontSize="sm" color={labelColor} mt={1}>
                            Todas as entradas provisórias já foram conciliadas.
                        </Text>
                    </Box>
                </Flex>
            ) : filtered.length === 0 ? (
                <Flex direction="column" align="center" gap={2} py={12} color="gray.400">
                    <Icon name="search" boxSize={8} />
                    <Text fontSize="sm">Nenhum resultado para "{search}"</Text>
                </Flex>
            ) : (
                <Accordion allowMultiple>
                    {filtered.map(invoice => (
                        <ProvisionalRow
                            key={invoice.id}
                            invoice={invoice}
                            onReconciled={refetch}
                        />
                    ))}
                </Accordion>
            )}
        </Container>
    )
}
